import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Loader2, MessageCircle, Pencil, Plus, Trash2, Users, User } from 'lucide-react';
import { produceAPI } from '@/lib/api';
import { OTHER, PRODUCE_CATEGORIES, PRODUCE_ITEMS, SELL_UNITS } from '@/data/produce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtDate, fmtQty, unitLabel, unitOne } from '@/components/produce/OfferCard';
import { useToast } from '@/hooks/use-toast';

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, '');
  return fallback;
}

/**
 * Sell produce: the farmer's available stock. Each entry is on the
 * produce market (sold by the farmer, through a cluster, or both) while
 * there is some left and its best-before date hasn't passed. Farmers keep
 * it up to date; an entry not checked for a week asks "Is this still right?".
 */
export default function SellProducePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null closed, {} new, listing to edit
  const [unread, setUnread] = useState(0);
  const lang = i18n.language;

  const load = useCallback(async () => {
    try {
      const [mine, un] = await Promise.all([produceAPI.mine(), produceAPI.unread()]);
      setListings(mine.data.listings);
      setUnread(un.data.unread);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load().catch(() => {}); }, [load]);

  const confirm = async (listing) => {
    await produceAPI.update(listing.id, { confirm: true });
    toast({ title: t('produceMarket.sell.saved') });
    load();
  };
  const remove = async (listing) => {
    if (!window.confirm(t('produceMarket.sell.confirmRemove', { item: listing.item }))) return;
    await produceAPI.close(listing.id);
    load();
  };

  return (
    <div className="p-4 md:p-6 animate-fadeIn pb-8 space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t('produceMarket.sell.title')}</h1>
        <p className="text-muted-foreground">{t('produceMarket.sell.subtitle')}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button className="h-14 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark" onClick={() => setEditing({})}
                data-testid="sell-add">
          <Plus className="w-5 h-5 mr-1" /> {t('produceMarket.sell.add')}
        </Button>
        <Button variant="outline" className="h-14 text-base bg-white relative" onClick={() => navigate('/produce/chats')}
                data-testid="sell-messages">
          <MessageCircle className="w-5 h-5 mr-1" /> {t('produceMarket.sell.messages')}
          {unread > 0 && <span className="ml-2 rounded-full bg-red-600 text-white text-xs px-2 py-0.5">{unread}</span>}
        </Button>
      </div>
      <Button variant="link" className="px-0 text-farm-primary" onClick={() => navigate('/produce')}>
        {t('produceMarket.sell.seeMarket')}
      </Button>

      {loading && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>}
      {!loading && listings.length === 0 && (
        <p className="text-center text-gray-600 py-8" data-testid="sell-none">{t('produceMarket.sell.none')}</p>
      )}
      <ul className="space-y-3" data-testid="sell-listings">
        {listings.map((l) => (
          <li key={l.id} className="rounded-2xl border bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-heading font-bold text-lg break-words">{l.item}</p>
                <p className="text-2xl font-extrabold text-farm-primary" data-testid="sell-qty">
                  {fmtQty(l.quantity, lang)} {unitLabel(t, l.unit)}
                </p>
              </div>
              <div className="flex">
                <Button size="icon" variant="ghost" onClick={() => setEditing(l)} aria-label={t('produceMarket.sell.edit')}
                        data-testid="sell-edit">
                  <Pencil className="w-5 h-5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(l)} aria-label={t('produceMarket.sell.remove')}>
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {t('produceMarket.market.bestBefore', { date: fmtDate(l.best_before, lang) })}
              {l.price != null && ` · ₦${fmtQty(l.price, lang)} / ${unitOne(t, l.unit)}`}
            </p>
            <p className="text-sm text-gray-600 flex flex-wrap gap-x-3">
              {l.sell_direct && <span className="flex items-center gap-1"><User className="w-4 h-4" />{t('produceMarket.sell.viaMe')}</span>}
              {l.cluster_name && <span className="flex items-center gap-1"><Users className="w-4 h-4" />{t('produceMarket.sell.via', { name: l.cluster_name })}</span>}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t('produceMarket.sell.updated', { date: fmtDate(l.updated_at, lang) })}</p>
            {!l.live && (
              <p className="mt-2 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">{t('produceMarket.sell.notLive')}</p>
            )}
            {l.stale && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="sell-stale">
                <p className="text-sm text-amber-900 flex items-center gap-1"><AlertCircle className="w-4 h-4" />
                  {t('produceMarket.sell.stale', { date: fmtDate(l.updated_at, lang) })}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="bg-farm-primary" onClick={() => confirm(l)} data-testid="sell-still-right">
                    <Check className="w-4 h-4 mr-1" /> {t('produceMarket.sell.stillRight')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(l)}>{t('produceMarket.sell.edit')}</Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <ListingDialog listing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

const EMPTY = { category: '', item: '', otherName: '', quantity: '', unit: '', best_before: '', price: '',
  sell_direct: true, via_cluster: false, cluster_id: '' };

function ListingDialog({ listing, onClose, onSaved }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [clusters, setClusters] = useState(null);
  const [saving, setSaving] = useState(false);
  const isNew = listing && !listing.id;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!listing) return;
    if (listing.id) {
      const listed = PRODUCE_ITEMS[listing.category]?.includes(listing.item);
      setForm({ category: listing.category, item: listed ? listing.item : OTHER, otherName: listed ? '' : listing.item,
                quantity: String(listing.quantity), unit: listing.unit, best_before: listing.best_before,
                price: listing.price != null ? String(listing.price) : '', sell_direct: listing.sell_direct,
                via_cluster: !!listing.cluster_id, cluster_id: listing.cluster_id || '' });
    } else {
      setForm(EMPTY);
    }
    if (clusters === null) {
      produceAPI.clusters().then((res) => setClusters(res.data.clusters)).catch(() => setClusters([]));
    }
  }, [listing, clusters]);

  const save = async (e) => {
    e.preventDefault();
    const item = (form.item === OTHER ? form.otherName : form.item).trim();
    if (!form.category || !item || !(Number(form.quantity) > 0) || !form.unit || !form.best_before) {
      toast({ title: t('produceMarket.sell.needAll'), variant: 'destructive' });
      return;
    }
    if (!form.sell_direct && !form.via_cluster) {
      toast({ title: t('produceMarket.sell.needHow'), variant: 'destructive' });
      return;
    }
    if (form.via_cluster && !form.cluster_id) {
      toast({ title: t('produceMarket.sell.needCluster'), variant: 'destructive' });
      return;
    }
    const price = form.price === '' ? null : Number(form.price);
    setSaving(true);
    try {
      if (isNew) {
        await produceAPI.add({ category: form.category, item, quantity: Number(form.quantity), unit: form.unit,
                               best_before: form.best_before, price, sell_direct: form.sell_direct,
                               cluster_id: form.via_cluster ? form.cluster_id : null });
      } else {
        await produceAPI.update(listing.id, {
          quantity: Number(form.quantity), unit: form.unit, best_before: form.best_before,
          ...(price == null ? { clear_price: true } : { price }), sell_direct: form.sell_direct,
          ...(form.via_cluster ? { cluster_id: form.cluster_id } : { clear_cluster: true }) });
      }
      toast({ title: t('produceMarket.sell.saved') });
      onSaved();
    } catch (err) {
      toast({ title: apiError(err, t('produceMarket.sell.failed')), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const chosenCluster = clusters?.find((c) => c.id === form.cluster_id);
  return (
    <Dialog open={!!listing} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? t('produceMarket.sell.formTitle') : t('produceMarket.sell.formEdit')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-base">{t('produce.categoryLabel')}</Label>
            <Select value={form.category} disabled={!isNew}
                    onValueChange={(v) => set({ category: v, item: '', otherName: '' })}>
              <SelectTrigger className="h-12 bg-white" data-testid="sell-category"><SelectValue placeholder={t('produce.categoryPlaceholder')} /></SelectTrigger>
              <SelectContent className="bg-white">
                {PRODUCE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`produce.categories.${c}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.category && (
            <div className="space-y-1.5">
              <Label className="text-base">{t(`produce.itemLabel.${form.category}`)}</Label>
              <Select value={form.item} disabled={!isNew} onValueChange={(v) => set({ item: v })}>
                <SelectTrigger className="h-12 bg-white" data-testid="sell-item"><SelectValue placeholder={t('produce.itemPlaceholder')} /></SelectTrigger>
                <SelectContent className="max-h-72 bg-white">
                  {PRODUCE_ITEMS[form.category].map((i) => <SelectItem key={i} value={i}>{i === OTHER ? t('produce.other') : i}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.item === OTHER && (
                <Input className="h-12" maxLength={120} disabled={!isNew} value={form.otherName}
                       placeholder={t('produce.otherPlaceholder')} onChange={(e) => set({ otherName: e.target.value })} />
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-base">{t('produceMarket.sell.quantity')}</Label>
              <Input type="number" inputMode="decimal" min="0" step="any" className="h-12" value={form.quantity}
                     onChange={(e) => set({ quantity: e.target.value })} data-testid="sell-quantity" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-base">{t('produceMarket.sell.unit')}</Label>
              <Select value={form.unit} onValueChange={(v) => set({ unit: v })}>
                <SelectTrigger className="h-12 bg-white" data-testid="sell-unit"><SelectValue placeholder={t('myProducts.choose')} /></SelectTrigger>
                <SelectContent className="bg-white">
                  {SELL_UNITS.map((u) => <SelectItem key={u} value={u}>{unitLabel(t, u)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-base">{t('produceMarket.sell.bestBefore')}</Label>
            <Input type="date" className="h-12" value={form.best_before} onChange={(e) => set({ best_before: e.target.value })}
                   data-testid="sell-best-before" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-base">{t('produceMarket.sell.price', { unit: form.unit ? unitOne(t, form.unit) : '…' })}</Label>
            <Input type="number" inputMode="decimal" min="0" className="h-12" value={form.price}
                   onChange={(e) => set({ price: e.target.value })} data-testid="sell-price" />
            <p className="text-xs text-gray-500">{t('produceMarket.sell.priceHint')}</p>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-base font-medium mb-1">{t('produceMarket.sell.sellHow')}</legend>
            <label className="flex items-center gap-3 rounded-xl border bg-white px-3 py-3">
              <input type="checkbox" className="w-5 h-5 accent-[#2e7d32]" checked={form.sell_direct}
                     onChange={(e) => set({ sell_direct: e.target.checked })} data-testid="sell-direct" />
              {t('produceMarket.sell.myself')}
            </label>
            <label className="flex items-center gap-3 rounded-xl border bg-white px-3 py-3">
              <input type="checkbox" className="w-5 h-5 accent-[#2e7d32]" checked={form.via_cluster}
                     onChange={(e) => set({ via_cluster: e.target.checked })} data-testid="sell-via-cluster" />
              {t('produceMarket.sell.cluster')}
            </label>
            {form.via_cluster && (
              clusters && clusters.length === 0 ? (
                <p className="text-sm text-amber-800">{t('produceMarket.sell.noClusters')}</p>
              ) : (
                <>
                  <Select value={form.cluster_id} onValueChange={(v) => set({ cluster_id: v })}>
                    <SelectTrigger className="h-12 bg-white text-left" data-testid="sell-cluster"><SelectValue placeholder={t('produceMarket.sell.chooseCluster')} /></SelectTrigger>
                    <SelectContent className="max-h-72 bg-white">
                      {(clusters || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {chosenCluster && (
                    <p className="text-sm text-gray-600" data-testid="sell-cluster-manager">
                      {t('produceMarket.sell.clusterManager', { name: chosenCluster.manager_name })}
                    </p>
                  )}
                </>
              )
            )}
          </fieldset>
          <Button type="submit" disabled={saving} className="w-full h-14 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark"
                  data-testid="sell-save">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : t('produceMarket.sell.save')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
